import type { Booking } from "../lib/types";
import type { BoardStore } from "./board";

export type BookingMode = "view" | "new" | null;

export interface UndoState {
  visible: boolean;
  booking: Booking | null;
  message: string;
}

export interface UIStore {
  bookingMode: BookingMode;
  editing: Booking | null;
  prefillStart: string | null;
  prefillEnd: string | null;
  prefillAircraftId: string | null;
  prefillAircraftSet: boolean;
  feedbackOpen: boolean;
  feedbackPromptVisible: boolean;
  feedbackDismissed: boolean;
  scheduleTipDismissed: boolean;
  undo: UndoState;
  openView(b: Booking): void;
  openNew(prefill?: { start?: string; end?: string; aircraftId?: string | null }): void;
  close(): void;
  openFeedback(): void;
  closeFeedback(): void;
  showFeedbackPrompt(): void;
  dismissFeedbackPrompt(): void;
  dismissScheduleTip(): void;
  showUndo(b: Booking, message: string): void;
  dismissUndo(): void;
  restoreUndo(): Promise<void>;
}

let undoTimer: ReturnType<typeof setTimeout> | null = null;

function getBoard(): BoardStore | null {
  const a = (window as unknown as { Alpine?: { store(name: string): unknown } }).Alpine;
  return (a?.store("board") as BoardStore) ?? null;
}

export function createUIStore(): UIStore {
  return {
    bookingMode: null,
    editing: null,
    prefillStart: null,
    prefillEnd: null,
    prefillAircraftId: null,
    prefillAircraftSet: false,
    feedbackOpen: false,
    feedbackPromptVisible: false,
    feedbackDismissed: false,
    scheduleTipDismissed: typeof localStorage !== "undefined" && localStorage.getItem("rs.tip.schedule") === "1",
    undo: { visible: false, booking: null, message: "" },
    openView(b: Booking) {
      this.editing = b;
      this.bookingMode = "view";
    },
    openNew(prefill) {
      this.editing = null;
      this.prefillStart = prefill?.start ?? null;
      this.prefillEnd = prefill?.end ?? null;
      if (prefill && "aircraftId" in prefill) {
        this.prefillAircraftId = prefill.aircraftId ?? null;
        this.prefillAircraftSet = true;
      } else {
        this.prefillAircraftId = null;
        this.prefillAircraftSet = false;
      }
      this.bookingMode = "new";
    },
    close() {
      this.bookingMode = null;
      this.editing = null;
      this.prefillStart = null;
      this.prefillEnd = null;
      this.prefillAircraftId = null;
      this.prefillAircraftSet = false;
    },
    openFeedback() {
      this.feedbackOpen = true;
      this.feedbackPromptVisible = false;
    },
    closeFeedback() {
      this.feedbackOpen = false;
    },
    showFeedbackPrompt() {
      if (this.feedbackDismissed || this.feedbackOpen) return;
      this.feedbackPromptVisible = true;
    },
    dismissFeedbackPrompt() {
      this.feedbackPromptVisible = false;
      this.feedbackDismissed = true;
    },
    dismissScheduleTip() {
      this.scheduleTipDismissed = true;
      if (typeof localStorage !== "undefined") localStorage.setItem("rs.tip.schedule", "1");
    },
    showUndo(b: Booking, message: string) {
      if (undoTimer) clearTimeout(undoTimer);
      this.undo = { visible: true, booking: b, message };
      undoTimer = setTimeout(() => {
        this.undo = { visible: false, booking: null, message: "" };
        undoTimer = null;
      }, 5000);
    },
    dismissUndo() {
      if (undoTimer) { clearTimeout(undoTimer); undoTimer = null; }
      this.undo = { visible: false, booking: null, message: "" };
    },
    async restoreUndo() {
      const b = this.undo.booking;
      if (!b) return;
      const board = getBoard();
      if (!board) return;
      await board.setStatus(b.id, b.status, b.ready);
      this.dismissUndo();
    },
  };
}
